import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Edit, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import type { ExpertTip, ExpertTipFilters } from '@/types/expert-tip';

interface ExpertTipsTableProps {
  tips: ExpertTip[];
  isLoading: boolean;
  isEmpty?: boolean;
  sortBy: ExpertTipFilters['sortBy'];
  sortOrder: ExpertTipFilters['sortOrder'];
  onSort: (column: ExpertTipFilters['sortBy']) => void;
}

function SortableHeader({
  column,
  label,
  sortBy,
  sortOrder,
  onSort,
}: {
  column: ExpertTipFilters['sortBy'];
  label: string;
  sortBy: ExpertTipFilters['sortBy'];
  sortOrder: ExpertTipFilters['sortOrder'];
  onSort: (column: ExpertTipFilters['sortBy']) => void;
}) {
  const isActive = sortBy === column;
  return (
    <th className="px-4 py-3 text-left">
      <button
        className="flex items-center gap-1 text-sm font-medium text-pine-ink-soft hover:text-pine-ink"
        onClick={() => onSort(column)}
      >
        <span>{label}</span>
        <div className="flex flex-col -space-y-1">
          <ChevronUp className={`w-3 h-3 ${isActive && sortOrder === 'asc' ? 'text-pine-pine' : ''}`} />
          <ChevronDown className={`w-3 h-3 ${isActive && sortOrder === 'desc' ? 'text-pine-pine' : ''}`} />
        </div>
      </button>
    </th>
  );
}

function ExpertTipStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    published: {
      label: 'Published',
      className: 'bg-white border-[1.5px] border-pine-approved text-pine-approved',
    },
    unpublished: {
      label: 'Unpublished',
      className: 'bg-white border-[1.5px] border-pine-review text-pine-review',
    },
    archived: {
      label: 'Archived',
      className: 'bg-white border-[1.5px] border-pine-draft text-pine-draft',
    },
  };
  const c = config[status] || config.unpublished;

  return (
    <span className={`inline-flex items-center rounded-pine-pill px-2.5 py-0.5 text-xs font-semibold border ${c.className}`}>
      {c.label}
    </span>
  );
}

export default function ExpertTipsTable({
  tips,
  isLoading,
  isEmpty = false,
  sortBy,
  sortOrder,
  onSort,
}: ExpertTipsTableProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="admin-card overflow-hidden">
        <div className="animate-pulse space-y-4 p-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-pine-l1 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="admin-card p-12 text-center">
        <h3 className="text-lg font-semibold text-pine-ink">No expert tips yet</h3>
        <p className="text-pine-ink-soft mt-2">Get started by creating your first expert tip</p>
        <button
          onClick={() => navigate('/admin/expert-tips/new')}
          className="admin-button-primary mt-4"
        >
          Create Your First Expert Tip
        </button>
      </div>
    );
  }

  if (tips.length === 0) {
    return (
      <div className="admin-card p-12 text-center">
        <h3 className="text-lg font-semibold text-pine-ink">No expert tips found</h3>
        <p className="text-pine-ink-soft mt-2">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="admin-card overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-pine-line">
            <SortableHeader column="title" label="Title" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-left text-sm font-medium text-pine-ink-soft">Expert</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-pine-ink-soft">Domain</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-pine-ink-soft">Status</th>
            <SortableHeader column="sortOrder" label="Sort Order" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <SortableHeader column="updatedAt" label="Updated" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-right text-sm font-medium text-pine-ink-soft">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-pine-line">
          {tips.map((tip, index) => (
            <motion.tr
              key={tip.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="hover:bg-pine-hover-item transition-colors"
            >
              <td className="px-4 py-3">
                <button
                  className="text-pine-ink hover:text-pine-pine font-medium text-left"
                  onClick={() => navigate(`/admin/expert-tips/${tip.id}`)}
                >
                  {tip.title}
                </button>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-pine-ink text-sm">{tip.expert.name}</span>
                  {tip.expert.status === 'inactive' && (
                    <span title="Expert profile is inactive">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-sm">
                {tip.domain ? (
                  <span className="text-pine-ink">{tip.domain.title}</span>
                ) : (
                  <span className="text-pine-ink-soft">—</span>
                )}
              </td>
              <td className="px-4 py-3">
                <ExpertTipStatusBadge status={tip.status} />
              </td>
              <td className="px-4 py-3 text-pine-ink text-sm">
                {tip.sortOrder ?? '—'}
              </td>
              <td className="px-4 py-3 text-pine-ink text-sm">
                {new Date(tip.updatedAt).toLocaleDateString()}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`/admin/expert-tips/${tip.id}`)}
                    className="p-1.5 rounded-pine-input hover:bg-pine-hover-item text-pine-ink-soft hover:text-pine-ink transition-colors"
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/admin/expert-tips/${tip.id}/edit`)}
                    className="p-1.5 rounded-pine-input hover:bg-pine-hover-item text-pine-ink-soft hover:text-pine-ink transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
